import { Col, Form, Modal } from "react-bootstrap";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import FloatingInput from "../compoment/ui/FloatingInput";
import PhoneInputWithCountry from "../compoment/ui/PhoneInputWithCountry";
import Loader from "../compoment/ui/Loader";
import { useForm } from "../constants/useForm";
import Email from "../compoment/icon/Email";
import EyeOpen from "../compoment/icon/EyeOpen";
import Google from "./../assets/img/Google (1).png";
import LogoBlack from "./../assets/img/MOSIDA-black.png";
import {
  authService,
  loginWithPhoneApi,
  sendPhoneVerificationApi,
  verifyPhoneVerificationApi,
} from "./auth/authService";
import { AuthUser, SigninFormValues } from "./auth/types";
import { validateSignin } from "./auth/validation";
import { persistAuthUser } from "../utils/authSession";
import {
  DEFAULT_COUNTRY_ISO2,
  getCountryByIso2,
} from "./auth/countryCodes";
import {
  allowOnlyOtpDigits,
  buildE164Phone,
  isPhoneValidForOtp,
} from "./auth/phoneUtils";

interface SigninModalProps {
  show: boolean;
  onHide: () => void;
  props?: Record<string, unknown>;
  setSignupModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setForgotPasswordModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setSigninModal?: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenSignup?: () => void;
  onOpenForgotPassword?: () => void;
  onGoogleAuth?: () => Promise<void> | void;
  onAuthSuccess?: (user: AuthUser) => Promise<void> | void;
  /** When set, shows a Skip control under Sign In (e.g. print-frame back flow). */
  onSkip?: () => void;
  skipLabel?: string;
  /** Set false when parent shows its own login-success UI. */
  showSuccessToast?: boolean;
}

const SigninModal: React.FC<SigninModalProps> = ({
  show,
  onHide,
  setSignupModal,
  setSigninModal,
  props,
  setForgotPasswordModal,
  onOpenSignup,
  onOpenForgotPassword,
  onGoogleAuth,
  onAuthSuccess,
  onSkip,
  skipLabel = "Skip",
  showSuccessToast = true,
}) => {
  const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [credentialError, setCredentialError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryIso2, setCountryIso2] = useState(DEFAULT_COUNTRY_ISO2);
  const [verificationCode, setVerificationCode] = useState("");
  const [showPhoneStep, setShowPhoneStep] = useState(true);
  const [pendingPhone, setPendingPhone] = useState("");

  const { values, handleChange } = useForm({
    email: "",
    password: "",
  });

  const typedValues = values as SigninFormValues;

  const dialCode =
    getCountryByIso2(countryIso2)?.dialCode ||
    getCountryByIso2(DEFAULT_COUNTRY_ISO2)!.dialCode;

  const handleCredentialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (credentialError) setCredentialError("");
    handleChange(e);
  };

  useEffect(() => {
    if (!show) {
      setPhoneNumber("");
      setCountryIso2(DEFAULT_COUNTRY_ISO2);
      setVerificationCode("");
      setShowPhoneStep(true);
      setPendingPhone("");
      setCredentialError("");
    }
  }, [show]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationError = validateSignin(typedValues);
    if (validationError) return toast.error(validationError);

    if (isSubmitting) return;
    setCredentialError("");
    setIsSubmitting(true);
    try {
      const user = await authService.signin(typedValues);
      persistAuthUser(user);
      // Call success before onHide so parents can read pending back-save flags
      // (onHide must not clear them before onAuthSuccess runs).
      if (showSuccessToast) {
        toast.success("Login successfully.");
      }
      await onAuthSuccess?.(user);
      onHide();
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Invalid credentials";
      setCredentialError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    const normalized = buildE164Phone(phoneNumber, dialCode);
    if (!isPhoneValidForOtp(normalized)) {
      return toast.error("Enter a valid phone number with at least 10 digits.");
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendPhoneVerificationApi({
        phone_number: normalized,
        purpose: "login",
      });
      setPendingPhone(normalized);
      setShowPhoneStep(false);
      setVerificationCode("");
      toast.success("Verification code sent.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Could not send code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendPhoneOtp = async () => {
    if (!pendingPhone) return;
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendPhoneVerificationApi({
        phone_number: pendingPhone,
        purpose: "login",
      });
      setVerificationCode("");
      toast.success("A new code was sent.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Could not resend code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyPhoneSignIn = async () => {
    if (!pendingPhone || verificationCode.length < 6) {
      return toast.error("Enter the 6-digit code.");
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await verifyPhoneVerificationApi({
        phone_number: pendingPhone,
        code: verificationCode,
      });
      const user = await loginWithPhoneApi({ phone_number: pendingPhone });
      persistAuthUser(user);
      // Call success before onHide so parents can read pending back-save flags
      // (onHide must not clear them before onAuthSuccess runs).
      if (showSuccessToast) {
        toast.success("Signed in successfully.");
      }
      await onAuthSuccess?.(user);
      onHide();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Verification failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginWithGoogle = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (onGoogleAuth) {
        await onGoogleAuth();
      } else {
        await authService.googleAuth();
      }
    } catch {
      toast.error("Google authentication failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && <Loader />}
      <Modal
        className="mosidaModal signinModal"
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={show}
        onHide={onHide}
      >
        <Modal.Header closeButton className="pb-md-0"></Modal.Header>
        <Modal.Body className="py-5">
          <Col md={10} className="mx-auto">
            <div className="text-center mb-4 pb-md-0  mt-5">
              <img
                className="pointer"
                style={{ width: 140 }}
                src={LogoBlack}
                alt="MOSIDA logo"
              />
            </div>

            <div className="sectionHeading text-md-center mt-5 mb-4 pb-2">
            Sign in to complete your purchase and <br /> save your art to your gallery
            </div>

            <Form onSubmit={handleSubmit}>
              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="example@gmail.com"
                label="E-mail"
                icon={Email}
                type="text"
                name="email"
                value={typedValues.email}
                autoComplete="email"
                onChange={handleCredentialChange}
              />
              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="•••••••••••••••••"
                label="Password"
                icon={EyeOpen}
                type="password"
                name="password"
                value={typedValues.password}
                autoComplete="current-password"
                onChange={handleCredentialChange}
                setVisiblePassword={setVisiblePassword}
                visiblePassword={visiblePassword}
              />
              {credentialError && (
                <p className="signinModal__credentialError" role="alert">
                  {credentialError}
                </p>
              )}
              <button
                className="btnPrimary w-100 sqaure"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing In..." : "Sign In"}
              </button>
              {onSkip && (
                <button
                  type="button"
                  className="signinModal__skip w-100 sqaure mt-3"
                  disabled={isSubmitting}
                  onClick={() => onSkip()}
                >
                  {skipLabel}
                </button>
              )}
            </Form>

            <div className="divider">
              <span className="divider-text">or sign in with</span>
            </div>

            {showPhoneStep ? (
              <PhoneInputWithCountry
                key="signin-phone-number"
                placeholder="Phone number"
                label="Phone number"
                name="phoneNumber"
                phoneNumber={phoneNumber}
                onPhoneNumberChange={setPhoneNumber}
                countryIso2={countryIso2}
                onCountryChange={setCountryIso2}
                inlineEndButton={{
                  label: isSubmitting ? "…" : "Send OTP",
                  onClick: handleSendPhoneOtp,
                  disabled: isSubmitting,
                  variant: "secondary",
                }}
              />
            ) : (
              <>
                <FloatingInput
                  key="signin-phone-code"
                  rootClassName="floatingInput--otpVerify"
                  placeholder="Enter verification code"
                  label="Verify OTP"
                  type="tel"
                  name="verificationCode"
                  value={verificationCode}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(e) =>
                    setVerificationCode(allowOnlyOtpDigits(e.target.value, 6))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleVerifyPhoneSignIn();
                    }
                  }}
                  inlineEndButton={{
                    label: isSubmitting ? "…" : "Verify & Sign In",
                    onClick: handleVerifyPhoneSignIn,
                    disabled: isSubmitting,
                    variant: "primary",
                  }}
                />
                <div className="text-center signinModal__resendWrap">
                  <button
                    type="button"
                    className="signinModal__resend"
                    onClick={handleResendPhoneOtp}
                    disabled={isSubmitting}
                  >
                    Resend code
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-link w-100 mt-1 mb-2 text-muted small"
                  onClick={() => {
                    setShowPhoneStep(true);
                    setVerificationCode("");
                  }}
                >
                  Change phone number
                </button>
              </>
            )}

            {/* <div className="socialLoginBtn">
              <button
                className="google"
                type="button"
                onClick={handleLoginWithGoogle}
                aria-label="Sign in with Google"
              >
                <img src={Google} alt="" />
                Sign in using google
              </button>
            </div> */}

            <div className="my-4 pt-md-2">
              <div className="dontHaveAccount">
                {`Don’t have an account?`}
                <span
                  className="text-primary"
                  onClick={() => {
                    onOpenSignup?.();
                    setSigninModal?.(false);
                    setSignupModal?.(true);
                  }}
                >
                  Sign up
                </span>
              </div>
              <div
                className="dontHaveAccount pointer"
                onClick={() => {
                  onOpenForgotPassword?.();
                  setSigninModal?.(false);
                  setForgotPasswordModal?.(true);
                }}
              >
                Forgot password?
              </div>
            </div>
          </Col>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SigninModal;
